import { useCallback, useEffect, useState } from "react";
import { Loader2, Plus, Sprout } from "lucide-react";
import { Button } from "@project/components/ui/button";
import { Checkbox } from "@project/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@project/components/ui/dialog";
import { Input } from "@project/components/ui/input";
import { Label } from "@project/components/ui/label";
import { Textarea } from "@project/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@project/components/ui/select";
import { MobileNumberField } from "@project/components/mobile-number-field";
import { SearchableSelect } from "@project/admin/components/searchable-select";
import { getApiErrorMessage } from "@project/lib/api-error";
import { projectToast } from "@project/lib/toast";
import { gaushalaDonationService } from "@project/services/admin/gaushala-donation.service";
import { gaushalaSchemeService } from "@project/services/admin/gaushala-scheme.service";
import {
  GAUSHALA_OCCASION_OPTIONS,
  GAUSHALA_OFFLINE_PAYMENT_MODE_OPTIONS,
  formatGaushalaPrice,
  type GaushalaAddDonationPayload,
} from "@project/types/gaushala";

type FormState = {
  scheme_id: string;
  donor_name: string;
  mobile_number: string;
  email: string;
  city: string;
  full_address: string;
  state: string;
  pincode: string;
  country: string;
  occasion: string;
  display_name: string;
  special_instructions: string;
  is_anonymous: boolean;
  info_confirmed: boolean;
  contact_consent: boolean;
  amount: string;
  payment_mode: string;
  payment_reference: string;
};

const initialForm: FormState = {
  scheme_id: "",
  donor_name: "",
  mobile_number: "",
  email: "",
  city: "Ahmedabad",
  full_address: "",
  state: "Gujarat",
  pincode: "",
  country: "India",
  occasion: "Birthday",
  display_name: "",
  special_instructions: "",
  is_anonymous: false,
  info_confirmed: true,
  contact_consent: true,
  amount: "",
  payment_mode: "cash",
  payment_reference: "",
};

type GaushalaAddDonationDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
};

export function GaushalaAddDonationDialog({
  open,
  onOpenChange,
  onSuccess,
}: GaushalaAddDonationDialogProps) {
  const [form, setForm] = useState<FormState>(initialForm);
  const [schemeLabel, setSchemeLabel] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(initialForm);
      setSchemeLabel("");
      setSubmitting(false);
    }
  }, [open]);

  const patch = (updates: Partial<FormState>) =>
    setForm(prev => ({ ...prev, ...updates }));

  const searchSchemes = useCallback(async (query: string) => {
    const result = await gaushalaSchemeService.listPage({
      page: 1,
      page_size: 50,
      search: query || undefined,
    });
    return (result.data ?? [])
      .filter(s => s.is_active !== false)
      .map(s => ({
        value: String(s.id),
        label: s.title,
        secondary: [s.short_title, formatGaushalaPrice(s.amount)]
          .filter(Boolean)
          .join(" · "),
      }));
  }, []);

  const buildPayload = (): GaushalaAddDonationPayload => {
    if (!form.donor_name.trim()) {
      throw new Error("Donor name is required.");
    }
    if (!/^\d{10}$/.test(form.mobile_number.trim())) {
      throw new Error("Enter a valid 10-digit mobile number.");
    }
    if (!form.email.trim()) {
      throw new Error("Email is required.");
    }
    if (!form.city.trim()) {
      throw new Error("City is required.");
    }
    if (!form.full_address.trim()) {
      throw new Error("Full address is required.");
    }
    if (!form.state.trim()) {
      throw new Error("State is required.");
    }
    if (!/^\d{6}$/.test(form.pincode.trim())) {
      throw new Error("Enter a valid 6-digit pincode.");
    }
    const numAmount = Number(form.amount);
    if (!Number.isFinite(numAmount) || numAmount <= 0) {
      throw new Error("Please enter a valid amount.");
    }
    if (!form.payment_mode) {
      throw new Error("Payment mode is required.");
    }
    if (!form.info_confirmed) {
      throw new Error("Please confirm the donation information.");
    }

    const schemeId = Number(form.scheme_id);

    return {
      scheme_id:
        Number.isFinite(schemeId) && schemeId > 0 ? schemeId : undefined,
      donor_name: form.donor_name.trim(),
      mobile_number: form.mobile_number.trim(),
      email: form.email.trim(),
      city: form.city.trim(),
      full_address: form.full_address.trim(),
      state: form.state.trim(),
      pincode: form.pincode.trim(),
      country: form.country.trim() || "India",
      occasion: form.occasion || undefined,
      display_name: form.display_name.trim() || undefined,
      special_instructions: form.special_instructions.trim() || undefined,
      is_anonymous: form.is_anonymous,
      info_confirmed: form.info_confirmed,
      contact_consent: form.contact_consent,
      amount: numAmount.toFixed(2),
      mode: "offline",
      payment_mode: form.payment_mode,
      payment_reference: form.payment_reference.trim() || undefined,
    };
  };

  const handleSubmit = async () => {
    try {
      const payload = buildPayload();
      setSubmitting(true);
      await gaushalaDonationService.add(payload);
      projectToast.success("Gaushala donation added successfully");
      onOpenChange(false);
      onSuccess();
    } catch (error) {
      projectToast.error(getApiErrorMessage(error, "Could not add donation"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[92vh] w-[calc(100vw-1.5rem)] max-w-3xl flex-col gap-0 overflow-hidden rounded-2xl p-0">
        <DialogHeader className="shrink-0 border-b border-border px-5 py-4 pr-12 text-left sm:px-6">
          <div className="flex items-start gap-3">
            <div className="bg-primary/10 grid h-10 w-10 shrink-0 place-items-center rounded-xl text-primary">
              <Sprout className="h-5 w-5" />
            </div>
            <div>
              <DialogTitle className="text-lg font-semibold sm:text-xl">
                Add Gaushala Donation
              </DialogTitle>
              <p className="mt-0.5 text-sm text-muted-foreground">
                Record an offline Gau Seva donation
              </p>
            </div>
          </div>
        </DialogHeader>

        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 py-5 sm:px-6">
          <section className="grid gap-3 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <Label>Gaushala Scheme (Optional)</Label>
              <SearchableSelect
                value={form.scheme_id || undefined}
                onValueChange={(value, option) => {
                  setSchemeLabel(option?.label ?? "");
                  patch({ scheme_id: value });
                  if (value) {
                    void gaushalaSchemeService.get(Number(value)).then(s => {
                      if (s?.amount) {
                        const amt = Number(s.amount);
                        if (Number.isFinite(amt)) {
                          patch({ amount: String(amt) });
                        }
                      }
                    });
                  }
                }}
                onSearch={searchSchemes}
                seedOption={
                  form.scheme_id
                    ? {
                        value: form.scheme_id,
                        label: schemeLabel || `Scheme #${form.scheme_id}`,
                      }
                    : undefined
                }
                placeholder="Select scheme (optional)..."
                searchPlaceholder="Search schemes..."
                emptyText="No schemes found."
                className="mt-1"
              />
            </div>

            <div>
              <Label data-required="true">Amount (₹)</Label>
              <Input
                type="number"
                min={0}
                step="0.01"
                value={form.amount}
                onChange={e => patch({ amount: e.target.value })}
                placeholder="5100.00"
                className="mt-1 rounded-xl"
              />
            </div>

            <div>
              <Label data-required="true">Payment Mode</Label>
              <Select
                value={form.payment_mode || undefined}
                onValueChange={value => patch({ payment_mode: value })}
              >
                <SelectTrigger className="mt-1 rounded-xl">
                  <SelectValue placeholder="Select payment mode..." />
                </SelectTrigger>
                <SelectContent>
                  {GAUSHALA_OFFLINE_PAYMENT_MODE_OPTIONS.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Occasion</Label>
              <Select
                value={form.occasion || undefined}
                onValueChange={value => patch({ occasion: value })}
              >
                <SelectTrigger className="mt-1 rounded-xl">
                  <SelectValue placeholder="Select occasion..." />
                </SelectTrigger>
                <SelectContent>
                  {GAUSHALA_OCCASION_OPTIONS.map(option => (
                    <SelectItem key={option} value={option}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Payment Reference</Label>
              <Input
                value={form.payment_reference}
                onChange={e => patch({ payment_reference: e.target.value })}
                placeholder="Cheque / UPI / NEFT reference"
                className="mt-1 rounded-xl"
              />
            </div>
          </section>

          <section>
            <h3 className="mb-3 font-serif text-lg font-semibold text-foreground">
              Donor Details
            </h3>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label data-required="true">Donor Name</Label>
                <Input
                  value={form.donor_name}
                  onChange={e => patch({ donor_name: e.target.value })}
                  placeholder="Anmol Patel"
                  className="mt-1 rounded-xl"
                />
              </div>
              <div>
                <Label>Display Name</Label>
                <Input
                  value={form.display_name}
                  onChange={e => patch({ display_name: e.target.value })}
                  placeholder="Anmol Family"
                  className="mt-1 rounded-xl"
                />
              </div>
              <div>
                <Label data-required="true">Mobile Number</Label>
                <MobileNumberField
                  required
                  value={form.mobile_number}
                  onChange={mobile_number => patch({ mobile_number })}
                  placeholder="10-digit phone number"
                  className="mt-1 rounded-xl"
                />
              </div>
              <div>
                <Label data-required="true">Email</Label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={e => patch({ email: e.target.value })}
                  placeholder="anmol@example.com"
                  className="mt-1 rounded-xl"
                />
              </div>
              <div className="sm:col-span-2">
                <Label data-required="true">Full Address</Label>
                <Textarea
                  value={form.full_address}
                  onChange={e => patch({ full_address: e.target.value })}
                  placeholder="Satellite Road, Ahmedabad"
                  className="mt-1 rounded-xl"
                  rows={2}
                />
              </div>
              <div>
                <Label data-required="true">City</Label>
                <Input
                  value={form.city}
                  onChange={e => patch({ city: e.target.value })}
                  placeholder="Ahmedabad"
                  className="mt-1 rounded-xl"
                />
              </div>
              <div>
                <Label data-required="true">State</Label>
                <Input
                  value={form.state}
                  onChange={e => patch({ state: e.target.value })}
                  placeholder="Gujarat"
                  className="mt-1 rounded-xl"
                />
              </div>
              <div>
                <Label data-required="true">Pincode</Label>
                <Input
                  value={form.pincode}
                  onChange={e => patch({ pincode: e.target.value })}
                  placeholder="380015"
                  maxLength={6}
                  className="mt-1 rounded-xl"
                />
              </div>
              <div>
                <Label>Country</Label>
                <Input
                  value={form.country}
                  onChange={e => patch({ country: e.target.value })}
                  placeholder="India"
                  className="mt-1 rounded-xl"
                />
              </div>
              <div className="sm:col-span-2">
                <Label>Special Instructions</Label>
                <Textarea
                  value={form.special_instructions}
                  onChange={e =>
                    patch({ special_instructions: e.target.value })
                  }
                  placeholder="Gaushala donation"
                  className="mt-1 rounded-xl"
                  rows={2}
                />
              </div>
            </div>
          </section>

          <section className="bg-muted/20 space-y-3 rounded-xl border border-border p-4">
            <label className="flex cursor-pointer items-start gap-3">
              <Checkbox
                checked={form.is_anonymous}
                onCheckedChange={checked =>
                  patch({ is_anonymous: checked === true })
                }
                className="mt-0.5"
              />
              <span className="text-sm text-foreground">
                Record as anonymous donation
              </span>
            </label>
            <label className="flex cursor-pointer items-start gap-3">
              <Checkbox
                checked={form.info_confirmed}
                onCheckedChange={checked =>
                  patch({ info_confirmed: checked === true })
                }
                className="mt-0.5"
              />
              <span className="text-sm text-foreground">
                Information confirmed{" "}
                <span className="text-destructive">*</span>
              </span>
            </label>
            <label className="flex cursor-pointer items-start gap-3">
              <Checkbox
                checked={form.contact_consent}
                onCheckedChange={checked =>
                  patch({ contact_consent: checked === true })
                }
                className="mt-0.5"
              />
              <span className="text-sm text-foreground">
                Contact consent for this donation
              </span>
            </label>
          </section>
        </div>

        <DialogFooter className="shrink-0 gap-2 border-t border-border px-5 py-4 sm:px-6">
          <Button
            type="button"
            variant="outline"
            className="rounded-xl"
            disabled={submitting}
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            className="gap-1.5 rounded-xl"
            disabled={submitting}
            onClick={() => void handleSubmit()}
          >
            {submitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Plus className="h-4 w-4" />
            )}
            {submitting ? "Saving…" : "Add Donation"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
